// Ядро клиента: снимок + журнал. docs/proposals/2026-09-06-collaboration-model.md.
//
// Загрузка: подписаться на change_log → снимок с max(id) → применить из буфера всё, что новее.
// События одной транзакции применяются разом. После разрыва — дочитывание по last_seen_id.
// Своя запись показывается сразу (слой ожидающих) и снимается, когда её tx пришёл журналом.
import { createStore as createZustand } from 'zustand/vanilla';
import { watchIdle } from './idle';
import { WriteBreaker } from './breaker';
import { HEARTBEAT_MS, RECONNECT_MIN_MS, RECONNECT_STABLE_MS, nextReconnectDelay, stalePresence } from './presence';
import { VIEWER_TABLES } from '../auth/roles';
import type { RealtimeChannel, SupabaseClient } from '@supabase/supabase-js';
import {
  emptyTables, isTableName, type AnyRow, type Change, type ChangeEvent, type Connection,
  type Op, type Snapshot, type TableMaps, type TableName,
} from './model';

export type PendingChange = { table: TableName; id: string; set: Record<string, unknown>; tx: string | null; at: number };

export type StoreState = {
  status: 'idle' | 'loading' | 'ready' | 'error';
  error: string | null;
  connection: Connection;
  lastSeenId: number;
  fromDate: string | null;
  tables: TableMaps;
  pending: PendingChange[];
  // сколько событий применили — для тестов и индикатора
  appliedEvents: number;
  // когда последний раз сверялись с журналом (снимок или дочитывание) — тултип аватара
  syncedAt: number | null;
  // я для присутствия и меню аккаунта: имя, цвет, картинка
  self: SelfInfo | null;
  // последний чужой tx и его автор: sync-тесты видят по нему, что чужой батч применён; History покажет «кто менял»
  lastForeign: { tx: string; actor: string | null; at: number; rows: { table: TableName; id: string }[] } | null;
  // личные настройки поверх общих; ключ → значение
  settings: Map<string, unknown>;
  // кто сейчас в приложении (по profile id)
  presence: Record<string, PresenceInfo>;
  // кто недавно вышел: бледный кружок с «last seen», пока не прошёл час; знает только эта вкладка
  recentlyLeft: Record<string, PresenceInfo & { left_at: number }>;
  // вкладка видна, но к ней давно не подходили: вышли из присутствия, экраны снимают выделение
  idle: boolean;
  // предохранитель записи сработал: текст для баннера, запись по этим строкам стоит до перезагрузки
  writesBlocked: string | null;
  // чужая правка по строке, которую я менял последние полминуты
  overridden: { table: TableName; id: string; actor: string | null; at: number } | null;
  // личная запись (настройка) не прошла: текст для баннера, снимается следующей удачной или крестиком
  notice: string | null;
};

export type PresenceInfo = {
  profile_id: string;
  name: string;
  color: string;
  avatar: string | null;      // картинка аккаунта Google; без неё — инициалы на цвете
  screen: string | null;
  dragging: string[];        // задачи в руках (групповой сдвиг — несколько)
  editing: string | null;    // id задачи с открытой карточкой
  selected: string[];        // выделенные задачи — у других они в рамке цвета человека
};

export const LEFT_MS = 60 * 60_000;     // сколько держать ушедшего бледным кружком
const SELECT_DEBOUNCE_MS = 200;         // рамка выделения меняет набор на каждом движении мыши

export type SelfInfo = { id: string; name: string; color: string; avatar?: string | null; role?: string };

export type ApplyResult = { ok: true; tx: string } | { ok: false; conflict: boolean; error: string };

export type Options = {
  fromDate?: string;
  txFlushMs?: number;        // сколько ждать хвост транзакции перед применением
  pauseAfterHiddenMs?: number;
  catchUpLimit?: number;     // больше этого — перезагружаем снимок
  pollMs?: number;           // страховочное дочитывание по таймеру: realtime может молча терять
  presenceLeaveMs?: number;  // через сколько после скрытия вкладки выходить из присутствия
  idleAfterMs?: number;      // без мыши и клавиатуры столько — тоже выходим и снимаем выделение
  breakerLimit?: number;     // столько отказов записи по одной строке за окно — запись по ней стоит до перезагрузки
  breakerWindowMs?: number;
  selfId?: string | null;    // profile id — чтобы отличать свои события
  self?: SelfInfo;           // для присутствия
};

const DEFAULTS = { txFlushMs: 200, pauseAfterHiddenMs: 30 * 60_000, catchUpLimit: 5000, pollMs: 60_000, presenceLeaveMs: 60_000, idleAfterMs: 15 * 60_000, breakerLimit: 5, breakerWindowMs: 10_000 };

const BLOCKED = 'Saving is paused after repeated failures on the same item — reload the page to continue.';
const READ_ONLY = 'You have view-only access — ask an admin for edit rights.';
const RECENT_MS = 30_000;

export function createStore(client: SupabaseClient, options: Options = {}) {
  const opts = { ...DEFAULTS, ...options };
  let selfId: string | null = options.selfId ?? null;

  let self: SelfInfo | null = options.self ?? null;
  if (self && !selfId) selfId = self.id;

  const store = createZustand<StoreState>(() => ({
    status: 'idle', error: null, connection: 'connecting', lastSeenId: 0, fromDate: null,
    tables: emptyTables(), pending: [], appliedEvents: 0, syncedAt: null, self: options.self ?? null, lastForeign: null,
    settings: new Map(), presence: {}, recentlyLeft: {}, idle: false, overridden: null, writesBlocked: null, notice: null,
  }));
  // мои недавние записи: строка → когда; чтобы заметить, что их перекрыли
  const myRecent = new Map<string, number>();

  // Строки «в руках»: события по ним откладываются до release.
  const held = new Set<string>();
  let deferred: ChangeEvent[] = [];
  const rowKey = (table: string, id: string) => `${table}:${id}`;
  const breaker = new WriteBreaker(opts.breakerLimit, opts.breakerWindowMs);

  let channel: RealtimeChannel | null = null;
  let buffer: ChangeEvent[] = [];          // события до снимка
  let ready = false;
  let txBatch: ChangeEvent[] = [];
  let txTimer: ReturnType<typeof setTimeout> | null = null;
  let hiddenTimer: ReturnType<typeof setTimeout> | null = null;
  let presenceTimer: ReturnType<typeof setTimeout> | null = null;
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let reconnectDelay = RECONNECT_MIN_MS;
  let stableTimer: ReturnType<typeof setTimeout> | null = null;
  let generation = 0;                      // номер подписки: статусы снятого канала — не наши
  let lastChannelNote = '';
  let catchingUp: Promise<void> | null = null;
  let stopped = false;

  // ------------------------------------------------------------------ применение

  function applyBatch(events: ChangeEvent[]) {
    if (events.length === 0) return;
    const s = store.getState();
    const tables = { ...s.tables };
    const touched = new Set<TableName>();
    let lastSeenId = s.lastSeenId;
    let pending = s.pending;
    let lastForeign = s.lastForeign;
    let overridden = s.overridden;
    const now = Date.now();
    const foreignRows: { table: TableName; id: string }[] = [];
    let applied = 0;

    for (const e of events) {
      if (e.id <= lastSeenId) continue;
      lastSeenId = e.id;
      applied++;
      if (!isTableName(e.table_name)) continue;
      const name = e.table_name;
      if (!touched.has(name)) {
        touched.add(name);
        (tables as Record<string, Map<string, unknown>>)[name] = new Map(tables[name] as Map<string, unknown>);
      }
      const map = tables[name] as Map<string, AnyRow>;
      const mine = selfId != null && e.actor === selfId;
      const key = rowKey(name, e.row_id);
      if (mine) myRecent.set(key, now);
      else if (e.kind === 'update' && (myRecent.get(key) ?? 0) > now - RECENT_MS) {
        overridden = { table: name, id: e.row_id, actor: e.actor, at: now };
      }
      if (!mine && held.has(key) && e.kind !== 'delete') {
        deferred.push(e);
      } else {
        applyRow(map, e);
      }

      if (!mine) foreignRows.push({ table: name, id: e.row_id });
    }
    // своё подтверждение: снять ожидающие с этим tx
    const txs = new Set(events.map((e) => e.tx));
    pending = pending.filter((p) => !(p.tx && txs.has(p.tx)));
    const last = events[events.length - 1];
    if (foreignRows.length > 0 && last) {
      lastForeign = { tx: last.tx, actor: last.actor, at: Date.now(), rows: foreignRows };
    }
    store.setState({ tables, lastSeenId, pending, lastForeign, overridden, appliedEvents: s.appliedEvents + applied });
  }

  function applyRow(map: Map<string, AnyRow>, e: ChangeEvent) {
    if (e.kind === 'insert' && e.new) map.set(e.row_id, e.new as AnyRow);
    else if (e.kind === 'update' && e.new) {
      const cur = map.get(e.row_id);
      if (cur) map.set(e.row_id, { ...cur, ...e.new });
    } else if (e.kind === 'delete') map.delete(e.row_id);
  }

  function hold(table: TableName, id: string) { held.add(rowKey(table, id)); }

  function release(table: TableName, id: string) {
    const key = rowKey(table, id);
    held.delete(key);
    const mine = deferred.filter((e) => rowKey(e.table_name, e.row_id) === key);
    if (mine.length === 0) return;
    deferred = deferred.filter((e) => !mine.includes(e));
    const s = store.getState();
    const tables = { ...s.tables };
    (tables as Record<string, Map<string, unknown>>)[table] = new Map(tables[table] as Map<string, unknown>);
    const map = tables[table] as Map<string, AnyRow>;
    for (const e of mine) applyRow(map, e);
    store.setState({ tables });
  }

  function flushTx() {
    if (txTimer) { clearTimeout(txTimer); txTimer = null; }
    const batch = txBatch;
    txBatch = [];
    applyBatch(batch);
  }

  // События одной транзакции приходят отдельными строками; копим, пока tx не сменился.
  function onEvent(e: ChangeEvent) {
    if (!ready) { buffer.push(e); return; }
    // id журнала монотонный: дыра перед пришедшим событием — значит, что-то не доставлено.
    // Дочитывание вернёт пропущенное (или ничего, если дыра от откаченной транзакции).
    const expected = Math.max(store.getState().lastSeenId, txBatch[txBatch.length - 1]?.id ?? 0) + 1;
    if (e.id > expected) { flushTx(); void catchUp().catch(() => undefined); return; }
    const head = txBatch[0];
    if (head && head.tx !== e.tx) flushTx();
    txBatch.push(e);
    if (txTimer) clearTimeout(txTimer);
    txTimer = setTimeout(flushTx, opts.txFlushMs);
  }

  // ------------------------------------------------------------------ снимок и дочитывание

  async function loadSnapshot() {
    const { data, error } = await client.rpc('get_snapshot', opts.fromDate ? { from_date: opts.fromDate } : {});
    if (error) throw new Error(error.message);
    const snap = data as unknown as Snapshot;
    const tables = emptyTables();
    for (const name of Object.keys(tables) as TableName[]) {
      const map = tables[name] as Map<string, AnyRow>;
      for (const row of (snap[name] ?? []) as AnyRow[]) map.set(row.id, row);
    }
    store.setState({ tables, lastSeenId: snap.log_id, fromDate: snap.from_date, status: 'ready', error: null, pending: [], syncedAt: Date.now() });
    ready = true;
    const late = buffer.filter((e) => e.id > snap.log_id).sort((a, b) => a.id - b.id);
    buffer = [];
    applyBatch(late);
  }

  // Одно дочитывание за раз: параллельные вызовы ждут текущее.
  function catchUp(): Promise<void> {
    if (catchingUp) return catchingUp;
    catchingUp = (async () => {
      const after = store.getState().lastSeenId;
      const { data, error } = await client.rpc('get_changes_after', { after_id: after, max_rows: opts.catchUpLimit + 1 });
      if (error) throw new Error(error.message);
      const rows = (data ?? []) as unknown as ChangeEvent[];
      if (rows.length > opts.catchUpLimit) {
        ready = false;
        await loadSnapshot();
        return;
      }
      flushTx();
      applyBatch(rows);
      store.setState({ syncedAt: Date.now() });
    })().finally(() => { catchingUp = null; });
    return catchingUp;
  }

  // Realtime после (пере)запуска может подтвердить подписку раньше, чем реально начнёт
  // доставлять; сообщения могут теряться и по другим причинам. Дешёвый опрос по таймеру
  // закрывает все такие дыры — это не замена потоку, а страховка.
  function startPolling() {
    if (pollTimer) clearInterval(pollTimer);
    pollTimer = setInterval(() => {
      const c = store.getState().connection;
      if (ready && c !== 'paused') void catchUp().catch(() => undefined);
    }, opts.pollMs);
  }

  // ------------------------------------------------------------------ настройки

  // Личные поверх общих. Общие — из снимка (shared_setting), личные — прямым чтением
  // user_setting (RLS отдаёт только свои).
  async function loadSettings() {
    const { data, error } = await client.from('user_setting').select('key, value');
    if (error) throw new Error(error.message);
    const settings = new Map<string, unknown>();
    for (const row of (data ?? []) as { key: string; value: unknown }[]) settings.set(row.key, row.value);
    store.setState({ settings });
  }

  function setting<T>(key: string, fallback: T): T {
    const s = store.getState();
    if (s.settings.has(key)) return s.settings.get(key) as T;
    const shared = [...s.tables.shared_setting.values()].find((r) => r.key === key);
    return shared ? (shared.value as T) : fallback;
  }

  // null — «как по умолчанию»: строки нет. Колонка value not null, и JS null туда не ложится.
  async function setSetting(key: string, value: unknown): Promise<void> {
    const prev = store.getState().settings;
    const settings = new Map(prev);
    if (value === null || value === undefined) settings.delete(key); else settings.set(key, value);
    store.setState({ settings });
    if (!selfId) return;
    const { error } = value === null || value === undefined
      ? await client.from('user_setting').delete().match({ profile_id: selfId, key })
      : await client.from('user_setting').upsert({ profile_id: selfId, key, value: value as never }, { onConflict: 'profile_id,key' });
    // не прошло — экран возвращается к прежнему, баннер говорит почему; молча разъехаться с базой нельзя
    if (error) { store.setState({ settings: prev, notice: `Your setting was not saved — ${error.message}` }); throw new Error(error.message); }
    if (store.getState().notice) store.setState({ notice: null });
  }

  // ------------------------------------------------------------------ присутствие

  let presenceChannel: RealtimeChannel | null = null;
  let myPresence: PresenceInfo | null = null;
  // Что человек делает сейчас — по broadcast, не по presence: presence.track ограничен
  // пятью вызовами за 30 секунд на клиента (ClientPresenceRateLimitReached), а начало
  // и конец каждого перетаскивания — уже два. Presence — только «кто здесь».
  // at — когда пакет пришёл: без свежих пакетов дольше двух тактов активность считается устаревшей (см. presence.ts)
  const activity = new Map<string, Pick<PresenceInfo, 'screen' | 'dragging' | 'editing' | 'selected'> & { at: number }>();
  let selectTimer: ReturnType<typeof setTimeout> | null = null;
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  let stopIdle: (() => void) | null = null;

  function publishPresence() {
    const state = presenceChannel?.presenceState<PresenceInfo>() ?? {};
    const presence: Record<string, PresenceInfo> = {};
    const now = Date.now();
    for (const [key, metas] of Object.entries(state)) {
      const last = metas[metas.length - 1];
      if (!last) continue;
      const seen = activity.get(key);
      const act = seen && !stalePresence(now, seen.at) ? seen : undefined;
      presence[key] = { ...last, avatar: last.avatar ?? null, screen: act?.screen ?? last.screen ?? null, dragging: act?.dragging ?? [], editing: act?.editing ?? null, selected: act?.selected ?? [] };
    }
    for (const key of activity.keys()) if (!(key in presence)) activity.delete(key);
    // вернулся — больше не «недавно вышел»; кто вышел давно — забываем
    const recentlyLeft = Object.fromEntries(Object.entries(store.getState().recentlyLeft).filter(([key, p]) => !(key in presence) && p.left_at > now - LEFT_MS));
    store.setState({ presence, recentlyLeft });
  }

  function rememberLeft(key: string) {
    const p = store.getState().presence[key];
    if (!p || key === self?.id) return;
    store.setState({ recentlyLeft: { ...store.getState().recentlyLeft, [key]: { ...p, dragging: [], editing: null, selected: [], left_at: Date.now() } } });
  }

  function broadcastActivity() {
    if (!presenceChannel || !myPresence) return;
    if (selectTimer) { clearTimeout(selectTimer); selectTimer = null; }
    const { profile_id, screen, dragging, editing, selected } = myPresence;
    void presenceChannel.send({ type: 'broadcast', event: 'activity', payload: { profile_id, screen, dragging, editing, selected } });
  }

  function joinPresence() {
    if (!self || presenceChannel) return;
    myPresence = myPresence ?? { profile_id: self.id, name: self.name, color: self.color, avatar: self.avatar ?? null, screen: null, dragging: [], editing: null, selected: [] };
    const { profile_id, name, color, avatar } = myPresence;
    presenceChannel = client
      .channel('presence', { config: { private: true, presence: { key: self.id } } }) // приватный: политики на realtime.messages пускают только участников домена (0027)
      .on('presence', { event: 'sync' }, publishPresence)
      // новичок не знает, что я уже что-то тащу или редактирую, — сказать ему
      .on('presence', { event: 'join' }, ({ key }) => { if (key !== profile_id) broadcastActivity(); })
      // sync после leave уже без ушедшего — запомнить его надо до этого
      .on('presence', { event: 'leave' }, ({ key }) => rememberLeft(key))
      .on('broadcast', { event: 'activity' }, ({ payload }) => {
        const p = payload as { profile_id: string; screen: string | null; dragging: string[] | string | null; editing: string | null; selected?: string[] };
        // клиент прошлой сборки шлёт один id или null и без selected
        const dragging = Array.isArray(p.dragging) ? p.dragging : p.dragging ? [p.dragging] : [];
        activity.set(p.profile_id, { screen: p.screen, dragging, editing: p.editing, selected: Array.isArray(p.selected) ? p.selected : [], at: Date.now() });
        publishPresence();
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          // один track на подключение: имя и цвет; остальное — broadcast
          void presenceChannel?.track({ profile_id, name, color, avatar, screen: null, dragging: [], editing: null, selected: [] });
          broadcastActivity();
          // heartbeat: своё состояние целиком раз в такт (потерянный пакет догоняется), чужое устаревшее — гаснет
          if (heartbeatTimer) clearInterval(heartbeatTimer);
          heartbeatTimer = setInterval(() => { broadcastActivity(); publishPresence(); }, HEARTBEAT_MS);
        }
      });
  }

  function leavePresence() {
    if (presenceChannel) { void client.removeChannel(presenceChannel); presenceChannel = null; }
    if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null; }
    activity.clear();
    if (selectTimer) { clearTimeout(selectTimer); selectTimer = null; }
    store.setState({ presence: {}, recentlyLeft: {} });
  }

  // Только при смене состояния — никаких таймеров и координат. Выделение — с задержкой:
  // рамка меняет набор на каждом движении, а слать нужно итог.
  function updatePresence(patch: Partial<Pick<PresenceInfo, 'screen' | 'dragging' | 'editing' | 'selected'>>) {
    if (!myPresence) return;
    const next = { ...myPresence, ...patch };
    const same = (k: 'dragging' | 'selected') => next[k].join() === myPresence![k].join();
    if (next.screen === myPresence.screen && same('dragging') && next.editing === myPresence.editing && same('selected')) return;
    const onlySelection = next.screen === myPresence.screen && same('dragging') && next.editing === myPresence.editing;
    myPresence = next;
    if (!onlySelection) { broadcastActivity(); return; }
    if (selectTimer) clearTimeout(selectTimer);
    selectTimer = setTimeout(broadcastActivity, SELECT_DEBOUNCE_MS);
  }

  // ------------------------------------------------------------------ подписка

  // Причина обрыва — в консоль, один раз на новый текст: иначе следующий такой случай снова разбирается вслепую.
  function noteChannel(status: string, err?: Error) {
    const note = err?.message ? `${status}: ${err.message}` : status;
    if (note === lastChannelNote) return;
    lastChannelNote = note;
    console.warn(`[roadmap] realtime change_log ${note}`);
  }

  // Имя канала с номером подписки: realtime-js по имени возвращает уже существующий канал,
  // и без номера новая подписка садилась на ещё не снятый старый.
  function subscribe(): Promise<void> {
    const gen = ++generation;
    return new Promise((resolve, reject) => {
      const ch = client
        .channel(`change_log:${gen}`)
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'change_log' }, (payload) => {
          onEvent(payload.new as ChangeEvent);
        })
        .subscribe((status, err) => {
          if (gen !== generation) return; // статус снятого канала — не наш
          if (status === 'SUBSCRIBED') {
            const wasDown = store.getState().connection !== 'connecting';
            store.setState({ connection: 'online' });
            if (stableTimer) clearTimeout(stableTimer);
            stableTimer = setTimeout(() => { reconnectDelay = RECONNECT_MIN_MS; }, RECONNECT_STABLE_MS);
            if (wasDown && ready) void catchUp().catch(() => undefined);
            resolve();
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            noteChannel(status, err);
            if (!ready) { reject(new Error(err?.message ?? status)); return; }
            scheduleResubscribe();
          } else if (status === 'CLOSED') {
            // сервер закрыл канал (истёк токен, пока ноутбук спал): realtime-js закрытый канал сам не поднимает
            noteChannel(status);
            if (!stopped && store.getState().connection !== 'paused') scheduleResubscribe();
          }
        });
      channel = ch;
    });
  }

  // Снять канал и дождаться подтверждения (с потолком: по мёртвому сокету leave не подтвердят),
  // чтобы его закрытие не пришло уже после новой подписки. Сразу поднимаем номер — хвосты игнорируются.
  async function unsubscribe() {
    const ch = channel;
    channel = null;
    generation++;
    if (stableTimer) { clearTimeout(stableTimer); stableTimer = null; }
    if (!ch) return;
    await Promise.race([client.removeChannel(ch).catch(() => undefined), new Promise((r) => setTimeout(r, 2000))]);
  }

  // Свой цикл переподписки: снять канал, обновить токен (getSession отдаёт свежий, setAuth — каналу),
  // подписаться снова с растущей паузой; после подписки дочитать журнал. Один таймер, без гонок с onVisibility.
  function scheduleResubscribe() {
    if (stopped || reconnectTimer || store.getState().connection === 'paused') return;
    store.setState({ connection: 'reconnecting' });
    reconnectTimer = setTimeout(async () => {
      reconnectTimer = null;
      if (stopped || store.getState().connection === 'paused') return;
      await unsubscribe();
      try {
        const { data } = await client.auth.getSession();
        if (data.session) client.realtime.setAuth(data.session.access_token);
      } catch { /* без сессии подписка отвалится сама и придёт сюда снова */ }
      reconnectDelay = nextReconnectDelay(reconnectDelay);
      // дочитывание журнала делает обработчик SUBSCRIBED (связь была не 'connecting') — здесь второго не нужно
      try { await subscribe(); } catch { scheduleResubscribe(); }
    }, reconnectDelay);
  }

  function fail(e: unknown) {
    store.setState({ status: 'error', error: (e as Error).message });
  }

  // ------------------------------------------------------------------ сеть пропала / вернулась (события браузера — мгновенно)

  function onOffline() {
    if (stopped || store.getState().connection !== 'online') return;
    store.setState({ connection: 'reconnecting' }); // плашка после DOWN_AFTER_MS, если не вернётся
  }
  function onOnline() {
    if (stopped || store.getState().connection === 'paused') return;
    if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    reconnectDelay = RECONNECT_MIN_MS;
    scheduleResubscribe();
  }

  // ------------------------------------------------------------------ вкладка не видна

  function onVisibility() {
    if (typeof document === 'undefined') return;
    if (document.hidden) {
      if (hiddenTimer) clearTimeout(hiddenTimer);
      hiddenTimer = setTimeout(() => {
        if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
        void unsubscribe();
        store.setState({ connection: 'paused' });
      }, opts.pauseAfterHiddenMs);
      // из присутствия — с задержкой: переключение окон не должно мигать, обед — должен
      if (presenceTimer) clearTimeout(presenceTimer);
      presenceTimer = setTimeout(leavePresence, opts.presenceLeaveMs);
    } else {
      if (hiddenTimer) { clearTimeout(hiddenTimer); hiddenTimer = null; }
      if (presenceTimer) { clearTimeout(presenceTimer); presenceTimer = null; }
      if (store.getState().connection === 'paused') {
        store.setState({ connection: 'connecting' });
        void subscribe().then(catchUp).catch(() => scheduleResubscribe());
      }
      if (!store.getState().idle) joinPresence(); // ушедший по бездействию вернётся первым движением, не переключением окон
    }
  }

  // ------------------------------------------------------------------ запись

  async function applyChanges(changes: Change[], op: Op): Promise<ApplyResult> {
    // Наблюдателю база откажет в любом случае; здесь — чтобы он увидел причину, а не код ошибки, и чтобы
    // оптимистичный слой не успел показать правку, которой не будет. Правила участника здесь не повторяем:
    // база тоньше клиента (участник заводит ненназначаемого человека из окна вопроса).
    if (self?.role === 'viewer' && changes.some((c) => !VIEWER_TABLES.has(c.table))) {
      return { ok: false, conflict: false, error: READ_ONLY };
    }
    const s = store.getState();
    const now = Date.now();
    const mine: PendingChange[] = changes
      .filter((c): c is Extract<Change, { set: Record<string, unknown> }> => 'set' in c)
      .map((c) => ({ table: c.table, id: c.id, set: c.set, tx: null, at: now }));
    // ключ отказа: строка по id; вставка без id — таблица целиком (зациклившийся insert иначе не остановить)
    const keys = changes.map((c) => rowKey(c.table, 'id' in c ? c.id : (typeof c.insert['id'] === 'string' ? c.insert['id'] : 'insert')));
    if (keys.some((k) => breaker.isOpen(k))) return { ok: false, conflict: false, error: BLOCKED };
    store.setState({ pending: [...s.pending, ...mine] });

    if (store.getState().connection !== 'online') {
      try { await catchUp(); } catch { /* сеть скажет ниже */ }
    }
    const { data, error } = await client.rpc('apply_changes', { changes, op });
    if (error) {
      store.setState({ pending: store.getState().pending.filter((p) => !mine.includes(p)) });
      const failedAt = Date.now();
      for (const k of keys) breaker.fail(k, failedAt);
      if (breaker.tripped && !store.getState().writesBlocked) store.setState({ writesBlocked: BLOCKED });
      return { ok: false, conflict: error.code === 'PT412', error: error.message };
    }
    const tx = String(data);
    for (const k of keys) breaker.ok(k);
    store.setState({ pending: store.getState().pending.map((p) => (mine.includes(p) ? { ...p, tx } : p)) });
    // Свой tx дочитываем сразу, не дожидаясь realtime: следующее действие человека должно
    // считаться на свежих строках (иначе expect_updated_at устареет и будет ложный conflict).
    try { await catchUp(); } catch { /* realtime или таймер доберут */ }
    setTimeout(() => {
      if (store.getState().pending.some((p) => p.tx === tx)) void catchUp().catch(() => undefined);
    }, 3000);
    return { ok: true, tx };
  }

  // ------------------------------------------------------------------ жизненный цикл

  async function start() {
    stopped = false;
    store.setState({ status: 'loading', error: null, connection: 'connecting' });
    try {
      // канал не поднялся — снимок всё равно грузим, подписку добираем циклом переподписки
      try { await subscribe(); } catch { scheduleResubscribe(); }
      await Promise.all([loadSnapshot(), loadSettings()]);
      startPolling();
      joinPresence();
      if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
      if (typeof window !== 'undefined') {
        window.addEventListener('offline', onOffline);
        window.addEventListener('online', onOnline);
        stopIdle = watchIdle(window, opts.idleAfterMs,
          () => { updatePresence({ selected: [], dragging: [] }); leavePresence(); store.setState({ idle: true }); },
          () => { store.setState({ idle: false }); if (!document.hidden) joinPresence(); });
      }
    } catch (e) {
      fail(e);
    }
  }

  function stop() {
    stopped = true;
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
    if (typeof window !== 'undefined') { window.removeEventListener('offline', onOffline); window.removeEventListener('online', onOnline); }
    if (stopIdle) { stopIdle(); stopIdle = null; }
    if (txTimer) clearTimeout(txTimer);
    if (hiddenTimer) clearTimeout(hiddenTimer);
    if (presenceTimer) clearTimeout(presenceTimer);
    if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    reconnectDelay = RECONNECT_MIN_MS;
    leavePresence();
    void unsubscribe();
    ready = false;
    buffer = [];
    txBatch = [];
    store.setState({ status: 'idle', connection: 'offline' });
  }

  // Строки таблицы с наложенными ожидающими правками — то, что показывает экран.
  function view<T extends TableName>(name: T): Map<string, StoreState['tables'][T] extends Map<string, infer R> ? R : never> {
    const s = store.getState();
    const base = s.tables[name] as Map<string, AnyRow>;
    const mine = s.pending.filter((p) => p.table === name);
    if (mine.length === 0) return base as never;
    const out = new Map(base);
    for (const p of mine) {
      const cur = out.get(p.id);
      // updated_by — мой: строка ещё не подтверждена журналом, но автор правки уже известен, иначе
      // открытая карточка примет мою же запись за чужую. updated_at не трогаем: на нём expect_updated_at.
      if (cur) out.set(p.id, { ...cur, ...p.set, ...(selfId ? { updated_by: selfId } : {}) });
    }
    return out as never;
  }

  function setSelf(info: SelfInfo | null) { self = info; selfId = info?.id ?? null; store.setState({ self: info }); }

  return {
    store, start, stop, applyChanges, catchUp, view, setSelf,
    hold, release, setting, setSetting, updatePresence,
  };
}

export type ClientStore = ReturnType<typeof createStore>;
